import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCars } from '../api/cars';

const DEBOUNCE_MS = 400;
const PAGE_SIZE = 12;

export const DEFAULT_FILTERS = {
  search: '',
  make: '',
  model: '',
  location: '',
  color: '',
  transmission: '',
  condition: 'all',
  bodyType: [],
  fuelType: [],
  yearMin: '',
  yearMax: '',
  priceMin: '',
  priceMax: '',
};

const normalizeServerFilters = (filters = {}) => {
  const next = {};
  const search = String(filters.search ?? '').trim();
  const make = String(filters.make ?? '').trim();
  const model = String(filters.model ?? '').trim();
  const location = String(filters.location ?? '').trim();
  const color = String(filters.color ?? '').trim();
  const condition = String(filters.condition ?? '').trim();
  const transmission = String(filters.transmission ?? '').trim();
  const yearMin = String(filters.yearMin ?? '').trim();
  const yearMax = String(filters.yearMax ?? '').trim();
  const priceMin = String(filters.priceMin ?? '').trim();
  const priceMax = String(filters.priceMax ?? '').trim();

  if (search) next.search = search;
  if (make) next.make = make;
  if (model) next.model = model;
  if (location) next.location = location;
  if (color) next.color = color;
  if (condition && condition !== 'all') next.condition = condition;
  if (transmission) next.transmission = transmission;
  if (yearMin) next.yearMin = yearMin;
  if (yearMax) next.yearMax = yearMax;
  if (priceMin) next.priceMin = priceMin;
  if (priceMax) next.priceMax = priceMax;

  const bodyType = Array.isArray(filters.bodyType) ? filters.bodyType.filter(Boolean) : [];
  const fuelType = Array.isArray(filters.fuelType) ? filters.fuelType.filter(Boolean) : [];

  if (bodyType.length) next.bodyType = bodyType;
  if (fuelType.length) next.fuelType = fuelType;

  return next;
};

const applyFilterChange = (prevFilters, key, value) => {
  const next = { ...prevFilters, [key]: value };

  if (key === 'make') next.model = '';

  return next;
};

const toggleInArray = (list, value) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

export function useVehicleFilters(config = {}) {
  const initialFilters = config.initialFilters ?? {};
  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...initialFilters,
    bodyType: Array.isArray(initialFilters.bodyType)
      ? initialFilters.bodyType
      : String(initialFilters.bodyType ?? '').split(',').filter(Boolean),
    fuelType: Array.isArray(initialFilters.fuelType)
      ? initialFilters.fuelType
      : String(initialFilters.fuelType ?? '').split(',').filter(Boolean),
  }));
  const [page, setPage] = useState(1);
  const [cars, setCars] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const setFilter = useCallback((key, value) => {
    setPage(1);
    setFilters((prev) => applyFilterChange(prev, key, value));
  }, []);

  const toggleArrayFilter = useCallback((key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: toggleInArray(prev[key] ?? [], value) }));
  }, []);

  const resetFilters = useCallback(() => {
    setPage(1);
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Individual filter setters
  const setSearch = useCallback((value) => setFilter('search', value), [setFilter]);
  const setMake = useCallback((value) => setFilter('make', value), [setFilter]);
  const setModel = useCallback((value) => setFilter('model', value), [setFilter]);
  const setYearMin = useCallback((value) => setFilter('yearMin', value), [setFilter]);
  const setYearMax = useCallback((value) => setFilter('yearMax', value), [setFilter]);
  const setPriceMin = useCallback((value) => setFilter('priceMin', value), [setFilter]);
  const setPriceMax = useCallback((value) => setFilter('priceMax', value), [setFilter]);
  const setLocation = useCallback((value) => setFilter('location', value), [setFilter]);
  const setColor = useCallback((value) => setFilter('color', value), [setFilter]);
  const setTransmission = useCallback((value) => setFilter('transmission', value), [setFilter]);
  const setCondition = useCallback((value) => setFilter('condition', value), [setFilter]);

  // Panel control
  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => !prev);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // Clear search only
  const clearSearchOnly = useCallback(() => {
    setSearch('');
  }, [setSearch]);

  // Submit search
  const submitSearch = useCallback((onSearch) => {
    if (onSearch) onSearch(filters);
    closePanel();
  }, [filters, closePanel]);

  // Update available models when make changes
  useEffect(() => {
    if (filters.make) {
      // TODO: Fetch models from API based on make
      // For now, just clear models when make changes
      setAvailableModels([]);
    } else {
      setAvailableModels([]);
    }
  }, [filters.make]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      const apiFilters = normalizeServerFilters(filters);

      fetchCars(apiFilters, page, PAGE_SIZE, controller.signal)
        .then((result) => {
          const payload = Array.isArray(result)
            ? { cars: result, totalPages: Math.max(1, Math.ceil(result.length / PAGE_SIZE)) }
            : (result ?? { cars: [], totalPages: 1 });

          setCars(payload.cars ?? []);
          setTotalPages(payload.totalPages ?? 1);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') setError(err.message);
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [filters, page]);

  return {
    filters,
    setFilter,
    toggleArrayFilter,
    resetFilters,
    setSearch,
    setMake,
    setModel,
    setYearMin,
    setYearMax,
    setPriceMin,
    setPriceMax,
    setLocation,
    setColor,
    setTransmission,
    setCondition,
    page,
    setPage,
    totalPages,
    cars,
    loading,
    error,
    isPanelOpen,
    availableModels,
    togglePanel,
    closePanel,
    clearSearchOnly,
    submitSearch,
  };
}

export default useVehicleFilters;